import { KIND_LABELS, validateSpec } from '@aurora/core'
import type { InteractiveKind, InteractiveSpec, Level } from '@aurora/core'
import { materialSpec } from '../store/selectors'

/**
 * The boundary between "the agent said this is an activity" and "the child can
 * play it".
 *
 * Everything that puts a generated material on screen comes through here, and
 * what comes out is the schema's *output* — a value zod built — never the
 * object the model authored passed along untouched. That is the whole safety
 * property Kid Mode rests on, so it lives in one function rather than being
 * re-implemented per surface.
 */

export type SpecRead =
  | { ok: true; spec: InteractiveSpec }
  | { ok: false; errors: string[] }

/** Validate the spec held by a material artifact. */
export function readSpec(artifact: unknown): SpecRead {
  const raw = materialSpec(artifact)
  if (raw === undefined || raw === null) {
    return { ok: false, errors: ['this one has no activity in it yet'] }
  }
  const result = validateSpec(raw)
  return result.ok ? { ok: true, spec: result.spec } : { ok: false, errors: result.errors }
}

export function isPlayable(artifact: unknown): boolean {
  return readSpec(artifact).ok
}

export function kindLabel(kind?: string): string {
  if (!kind) return 'Activity'
  return KIND_LABELS[kind as InteractiveKind] ?? humanize(kind)
}

function humanize(key: string): string {
  const words = key.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Difficulty in the parent's words.
 *
 * Deliberately about the work, not about her: "a stretch" describes a puzzle,
 * "advanced" would describe a child. Same five levels, no ranking of the kid.
 */
export const DIFFICULTY_LABELS: Record<Level, string> = {
  1: 'Very gentle',
  2: 'Gentle',
  3: 'Right at her level',
  4: 'A stretch',
  5: 'A big stretch',
}

export function difficultyLabel(level?: number): string {
  const n = Math.min(5, Math.max(1, Math.round(level ?? 3))) as Level
  return DIFFICULTY_LABELS[n]
}

/**
 * Which kinds make a usable sheet of paper.
 *
 * The rest are motion, sound or 3D — printing them would hand the parent a
 * screenshot and call it a worksheet, so the action is offered only where it
 * produces something real.
 */
const PRINTABLE: ReadonlySet<string> = new Set<InteractiveKind>([
  'tangram',
  'matrix_reasoning',
  'sight_word_deck',
  'counting_game',
  'pattern_builder',
  'deduction_puzzle',
  'sorting_game',
  'sequence_order',
  'letter_tracing',
])

export function isPrintable(kind?: string): boolean {
  return !!kind && PRINTABLE.has(kind)
}
